import {
  Columns,
  DottedText,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  CategoryData,
  CategoryDetailsData,
} from '../../pages/PaymentIntegrityPage/PaymentIntegrity.types';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React from 'react';
import { numberToCurrencyFormatter } from './helper';
import { cellStyles4 } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import { getTextWithToolTip } from './riskPatientDetailsHelper';
import SubDetailsGrid from '../../_components/atoms/SubDetailsGrid';

 
type CategoryRowBuilder = (cat: CategoryDetailsData) => any[];
type ColumnKey =
  | 'GROUP'
  | 'NO_OF_CLAIMS'
  | 'PLAN_PAID'
  | 'MEMBER_PAID'
  | 'TOTAL_PAID'
  | 'REASON';

const getColumnName = (name: string) => {
  return (
    <Typography
      fontSize="12px"
      fontType="medium"
      lineHeight="20px"
      textColor={patientAccessTheme.palette.status.white}
    >
      {name}
    </Typography>
  );
};

export const getPaymentIntegrityCategoryColumns = () => {
  const columns: Columns[] = [
    {
      name: 'Category',
      align: 'left',
      width: '350px',
      cellStyles: cellStyles4,
    },
    {
      name: 'No. of Claims',
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: 'Opportunity Plan Paid ($)',
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: 'Opportunity Member Paid ($)',
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
    {
      name: 'Opportunity Paid Amount ($)',
      align: 'left',
      width: '',
      cellStyles: {
        ...cellStyles4,
        color: patientAccessTheme.palette.text.lowemphasis,
      },
    },
  ];
  return columns;
};

export const formatCategories = (
  categories: CategoryData[],
   
  handleHistory: (category: string) => void,
) => {
  const data: any[] = [];

  categories.map((category) => {
    const row = [];
    row.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.primary.main}
        styleProps={{
          textDecoration: 'underline dashed',
          textUnderlineOffset: '6px',
          cursor: 'pointer',
        }}
        onClick={() => {
          handleHistory(category.category);
        }}
      >
        {category?.category}
      </Typography>,
    );
    row.push(category?.noOfClaims);
    row.push(numberToCurrencyFormatter(category?.planPaid));
    row.push(numberToCurrencyFormatter(category?.memberPaid));
    row.push(numberToCurrencyFormatter(category?.totalPaid));

    data.push(row);
  });
  return data;
};

const BASE_COLUMNS: Record<ColumnKey, Columns> = {
  GROUP: {
    name: '', // will be injected
    align: 'left',
    width: '',
    cellStyles: cellStyles4,
    cellPadding: '8px 18px 8px 48px',
    showEllipsis: true,
  },

  NO_OF_CLAIMS: {
    name: getColumnName('No. of Claims'),
    align: 'left',
    width: '',
    cellStyles: {
      ...cellStyles4,
      color: patientAccessTheme.palette.text.lowemphasis,
    },
  },

  PLAN_PAID: {
    name: getColumnName('Opportunity Plan Paid ($)'),
    align: 'left',
    width: '',
    cellStyles: {
      ...cellStyles4,
      color: patientAccessTheme.palette.text.lowemphasis,
    },
  },

  MEMBER_PAID: {
    name: getColumnName('Opportunity Member Paid ($)'),
    align: 'left',
    width: '',
    cellStyles: {
      ...cellStyles4,
      color: patientAccessTheme.palette.text.lowemphasis,
    },
  },

  TOTAL_PAID: {
    name: getColumnName('Opportunity Paid Amount ($)'),
    align: 'left',
    width: '',
    cellStyles: {
      ...cellStyles4,
      color: patientAccessTheme.palette.text.lowemphasis,
    },
  },

  REASON: {
    name: 'Reason',
    align: 'left',
    width: '25%',
    cellStyles: {
      ...cellStyles4,
      color: patientAccessTheme.palette.text.lowemphasis,
    },
  },
};

const CATEGORY_COLUMN_LAYOUTS: Record<string, ColumnKey[]> = {
  DEFAULT: ['GROUP', 'NO_OF_CLAIMS', 'PLAN_PAID', 'MEMBER_PAID', 'TOTAL_PAID'],

  'Patient Eligibility Checks': [
    'GROUP',
    'REASON',
    'NO_OF_CLAIMS',
    'PLAN_PAID',
    'MEMBER_PAID',
    'TOTAL_PAID',
  ],

  'NCCI Medically Unlikely Edits (MUEs)': [
    'GROUP',
    'NO_OF_CLAIMS',
    'PLAN_PAID',
    'MEMBER_PAID',
    'TOTAL_PAID',
  ],
};

const CATEGORY_COLUMN_OVERRIDES: Record<
  string,
  Partial<Record<ColumnKey, Partial<Columns>>>
> = {
  'NCCI Medically Unlikely Edits (MUEs)': {
    PLAN_PAID: { name: getColumnName('Additional Plan Paid ($)') },
    MEMBER_PAID: { name: getColumnName('Additional Member Paid ($)') },
    TOTAL_PAID: { name: getColumnName('Additional Paid Amount ($)') },
  },
};

export const getPaymentIntegrityCategoryDetailsColumns = (
  columnName: string,
  category: string,
): Columns[] => {
  const layout =
    CATEGORY_COLUMN_LAYOUTS[category] ?? CATEGORY_COLUMN_LAYOUTS.DEFAULT;

  const overrides = CATEGORY_COLUMN_OVERRIDES[category] ?? {};

  return layout.map((key) => {
    const baseColumn = BASE_COLUMNS[key];

    if (!baseColumn) {
      throw new Error(`Unknown column key: ${key}`);
    }

    if (key === 'GROUP') {
      return {
        ...baseColumn,
        name: columnName,
      };
    }

    return {
      ...baseColumn,
      ...overrides[key],
    };
  });
};

const categorySpecificRowData: Record<string, CategoryRowBuilder> = {
  'Patient Eligibility Checks': (cat) => [
    getTextWithToolTip(cat?.subCategory, 40),
  ],
};

export const formatCategoryDetails = (
  category: CategoryDetailsData[],
  selectedCategory: string,
) => {
  const data = category.map((cat) => {
    const width = [
      'NCCI Medically Unlikely Edits (MUEs)',
      'NCCI Procedure-To-Procedure (PTP) Edits',
    ].includes(selectedCategory)
      ? '350px'
      : '200px';
    const baseRow = [
      <DottedText
        text={cat.groupKey}
        typographyStyles={{
          fontSize: '14px',
          lineHeight: '24px',
          fontWeight: 500,
          color: patientAccessTheme.palette.primary[500],
        }}
        truncateText
        truncateWidth={width}
      />,
      cat?.noOfClaims,
      numberToCurrencyFormatter(cat?.planPaid),
      numberToCurrencyFormatter(cat?.memberPaid),
      numberToCurrencyFormatter(cat?.totalPaid),
      <SubDetailsGrid subDetails={cat?.subDetails} />,
    ];

    const categoryCells =
      categorySpecificRowData[selectedCategory]?.(cat) ?? [];

    return [baseRow[0], ...categoryCells, ...baseRow.slice(1)];
  });

  return data;
};
export const getSummaryData = (categories: CategoryData[]) => {
  let noOfClaims = 0;
  let totalPaid = 0;

  categories.map((cat: CategoryData) => {
    noOfClaims += cat.noOfClaims;
    totalPaid += cat.totalPaid;
  });
  return {
    noOfClaims,
    totalPaid,
  };
};
